import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Phone, Mail, MapPin, Clock, CheckCircle, Star, ArrowRight, Globe, Plane, GraduationCap, DollarSign } from 'lucide-react';
import UniversalFormWrapper from '../components/UniversalFormWrapper';

interface AbroadConsultancyFormData {
  name: string;
  email: string;
  phone: string;
  preferredCountry: string;
  courseType: string;
  studyLevel: string;
  budget: string;
  englishProficiency: string;
  targetIntake: string;
  previousEducation: string;
  workExperience: string;
  message: string;
  referralId?: string;
  referralName?: string;
  referralPhone?: string;
}

const AbroadConsultancyForm: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset } = useForm<AbroadConsultancyFormData>();

  const onSubmit = (data: AbroadConsultancyFormData) => {
    const subject = encodeURIComponent('Abroad Consultancy Application');
    const body = encodeURIComponent(
      `Full Name: ${data.name}\n` +
      `Email: ${data.email}\n` +
      `Phone: ${data.phone}\n` +
      `Preferred Country: ${data.preferredCountry}\n` +
      `Course Type: ${data.courseType}\n` +
      `Study Level: ${data.studyLevel}\n` +
      `Budget: ${data.budget}\n` +
      `English Proficiency: ${data.englishProficiency}\n` +
      `Target Intake: ${data.targetIntake}\n` +
      `Previous Education: ${data.previousEducation}\n` +
      `Work Experience: ${data.workExperience}\n` +
      `Message: ${data.message || ''}\n` +
      (data.referralId ? `Referral ID: ${data.referralId}\n` : '') +
      (data.referralName ? `Referral Name: ${data.referralName}\n` : '') +
      (data.referralPhone ? `Referral Phone: ${data.referralPhone}\n` : '')
    );
    const mailtoLink = `mailto:infonammasewa@gmail.com?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
  };

  const countries = [
    "United States", "Canada", "United Kingdom", "Australia", "New Zealand",
    "Germany", "France", "Netherlands", "Ireland", "Singapore", "Japan",
    "South Korea", "Sweden", "Norway", "Denmark", "Switzerland"
  ];

  const courseTypes = [
    "Medicine (MBBS/MD)", "Engineering", "Computer Science", "Business Administration",
    "Data Science", "Artificial Intelligence", "Biotechnology", "Pharmacy",
    "Nursing", "Dentistry", "Law", "Psychology", "Architecture", "Arts & Design"
  ];

  const studyLevels = [
    "Undergraduate (Bachelor's)", "Postgraduate (Master's)", "Doctorate (PhD)",
    "Diploma", "Certificate Course", "Foundation Program"
  ];

  const budgetRanges = [
    "₹10-20 Lakhs", "₹20-30 Lakhs", "₹30-50 Lakhs", "₹50-75 Lakhs", "₹75+ Lakhs"
  ];

  const englishTests = [
    "IELTS", "TOEFL", "PTE", "Duolingo", "Not taken yet", "Planning to take"
  ];

  const intakes = [
    "Fall 2024", "Spring 2025", "Fall 2025", "Spring 2026", "Not decided yet"
  ];

  const educationLevels = [
    "12th Grade/High School", "Bachelor's Degree", "Master's Degree", "Diploma", "Other"
  ];

  const workExperienceOptions = [
    "No experience", "0-2 years", "2-5 years", "5+ years"
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 }
  };

  const benefits = [
    {
      icon: <Globe className="h-6 w-6" />,
      title: "Global University Network",
      description: "Access to 500+ partner universities worldwide"
    },
    {
      icon: <Plane className="h-6 w-6" />,
      title: "Visa Assistance",
      description: "Complete visa guidance with 95% success rate"
    },
    {
      icon: <GraduationCap className="h-6 w-6" />,
      title: "Scholarship Support",
      description: "Help secure scholarships worth up to $50,000"
    },
    {
      icon: <DollarSign className="h-6 w-6" />,
      title: "Financial Planning",
      description: "Education loan assistance and budget planning"
    }
  ];

  return (
    <UniversalFormWrapper 
      formType="abroad" 
      source="AbroadConsultancyForm" 
      priority="high"
    >
      <div className="min-h-screen relative font-sans antialiased bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 text-white py-16 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeInUp}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight">
              Study Abroad Consultancy
            </h1>
            <p className="text-2xl md:text-3xl mb-8 text-cyan-200">
              Your Gateway to World-Class International Education
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#abroad-form"
                className="bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-300 px-8 py-3 rounded-lg font-semibold hover:scale-105 hover:ring-2 hover:ring-cyan-400 transition inline-flex items-center justify-center backdrop-blur-lg border border-cyan-400/20 shadow-md"
              >
                Start Your Journey
                <Plane className="ml-2 h-5 w-5 text-cyan-300" />
              </a>
              <a
                href="/premium-services"
                className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 px-8 py-3 rounded-lg font-semibold hover:scale-105 hover:ring-2 hover:ring-purple-400 transition inline-flex items-center justify-center backdrop-blur-lg border border-purple-400/30 shadow-md"
              >
                Domestic Consultancy
                <ArrowRight className="ml-2 h-5 w-5 text-purple-300" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight">
              Why Choose Us for Study Abroad?
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-cyan-200">
              We provide end-to-end support for your international education journey
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: index * 0.1 }}
                className="backdrop-blur-lg bg-gradient-to-br from-cyan-400/5 to-purple-400/5 border border-cyan-400/10 rounded-xl shadow-md p-6 hover:scale-105 hover:ring-2 hover:ring-cyan-400 transition cursor-pointer text-center"
              >
                <div className="mb-4 text-cyan-400 flex justify-center">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-cyan-100">
                  {benefit.title}
                </h3>
                <p className="text-cyan-200 text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="abroad-form" className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight">
              Start Your Study Abroad Journey
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-cyan-200">
              Fill out this comprehensive form and our study abroad experts will create a personalized plan for you
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <motion.div
              {...fadeInUp}
              className="lg:col-span-1"
            >
              <div className="backdrop-blur-lg bg-gradient-to-br from-cyan-400/5 to-purple-400/5 border border-cyan-400/10 rounded-xl shadow-md p-6">
                <h3 className="text-xl font-semibold mb-6 text-pink-400">Contact Our Experts</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-cyan-400" />
                    <span className="text-cyan-200">+91 77086 77132</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-cyan-400" />
                    <span className="text-cyan-200">infonammasewa@gmail.com</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-cyan-400" />
                    <span className="text-cyan-200">SEWA International Office, Tamil Nadu, India</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Clock className="h-5 w-5 text-cyan-400" />
                    <span className="text-cyan-200">Mon - Sat: 10:00 AM - 6:00 PM</span>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-gradient-to-r from-green-900/20 to-blue-900/20 border border-green-500/30 rounded-lg">
                  <h4 className="font-semibold mb-2 text-green-400 flex items-center">
                    <Star className="h-4 w-4 mr-2" />
                    Success Stories
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="border-l-2 border-cyan-400 pl-3">
                      <p className="text-cyan-200">"Got admission to Harvard with $30K scholarship!"</p>
                      <p className="text-cyan-400 text-xs">- Rahul, MBBS Student</p>
                    </div>
                    <div className="border-l-2 border-purple-400 pl-3">
                      <p className="text-purple-200">"Studying AI at MIT, thanks to SEWA!"</p>
                      <p className="text-purple-400 text-xs">- Priya, CS Student</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              {...fadeInUp}
              className="lg:col-span-2"
            >
              <div className="backdrop-blur-lg bg-gradient-to-br from-cyan-400/5 to-purple-400/5 border border-cyan-400/10 rounded-xl shadow-md p-8">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
                    <h3 className="text-2xl font-semibold text-white mb-4">
                      Application Submitted Successfully!
                    </h3>
                    <p className="text-gray-300 mb-4">
                      Thank you for choosing SEWA for your study abroad journey. Our international education experts will review your application and contact you within 24 hours.
                    </p>
                    <div className="space-y-2 text-sm text-cyan-300">
                      <p>📧 Check your email for confirmation details</p>
                      <p>📱 Save our WhatsApp: +91 77086 77132</p>
                      <p>🎯 Your personalized study plan will be ready soon!</p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Personal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Full Name *
                        </label>
                        <input
                          {...register('name', { required: 'Name is required' })}
                          type="text"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          placeholder="Enter your full name"
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Email Address *
                        </label>
                        <input
                          {...register('email', {
                            required: 'Email is required',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address'
                            }
                          })}
                          type="email"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          placeholder="Enter your email"
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Phone Number *
                        </label>
                        <input
                          {...register('phone', { required: 'Phone number is required' })}
                          type="tel"
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          placeholder="Enter your phone number"
                        />
                        {errors.phone && (
                          <p className="mt-1 text-sm text-red-400">{errors.phone.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Preferred Country *
                        </label>
                        <select
                          {...register('preferredCountry', { required: 'Please select a country' })}
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        >
                          <option value="">Select preferred country</option>
                          {countries.map((country) => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                        {errors.preferredCountry && (
                          <p className="mt-1 text-sm text-red-400">{errors.preferredCountry.message}</p>
                        )}
                      </div>
                    </div>

                    {/* Academic Information */}
                    <div className="border-t border-gray-600 pt-6">
                      <h4 className="text-lg font-medium text-cyan-300 mb-4">Academic Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Course Type *
                          </label>
                          <select
                            {...register('courseType', { required: 'Please select a course type' })}
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          >
                            <option value="">Select course type</option>
                            {courseTypes.map((course) => (
                              <option key={course} value={course}>{course}</option>
                            ))}
                          </select>
                          {errors.courseType && (
                            <p className="mt-1 text-sm text-red-400">{errors.courseType.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Study Level *
                          </label>
                          <select
                            {...register('studyLevel', { required: 'Please select study level' })}
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          >
                            <option value="">Select study level</option>
                            {studyLevels.map((level) => (
                              <option key={level} value={level}>{level}</option>
                            ))}
                          </select>
                          {errors.studyLevel && (
                            <p className="mt-1 text-sm text-red-400">{errors.studyLevel.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Previous Education *
                          </label>
                          <select
                            {...register('previousEducation', { required: 'Please select your education level' })}
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          >
                            <option value="">Select education level</option>
                            {educationLevels.map((level) => (
                              <option key={level} value={level}>{level}</option>
                            ))}
                          </select>
                          {errors.previousEducation && (
                            <p className="mt-1 text-sm text-red-400">{errors.previousEducation.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Work Experience
                          </label>
                          <select
                            {...register('workExperience')}
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          >
                            <option value="">Select work experience</option>
                            {workExperienceOptions.map((exp) => (
                              <option key={exp} value={exp}>{exp}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Financial & Timeline Information */}
                    <div className="border-t border-gray-600 pt-6">
                      <h4 className="text-lg font-medium text-cyan-300 mb-4">Financial & Timeline</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Budget Range *
                          </label>
                          <select
                            {...register('budget', { required: 'Please select your budget range' })}
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          >
                            <option value="">Select budget range</option>
                            {budgetRanges.map((budget) => (
                              <option key={budget} value={budget}>{budget}</option>
                            ))}
                          </select>
                          {errors.budget && (
                            <p className="mt-1 text-sm text-red-400">{errors.budget.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            Target Intake *
                          </label>
                          <select
                            {...register('targetIntake', { required: 'Please select target intake' })}
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          >
                            <option value="">Select target intake</option>
                            {intakes.map((intake) => (
                              <option key={intake} value={intake}>{intake}</option>
                            ))}
                          </select>
                          {errors.targetIntake && (
                            <p className="mt-1 text-sm text-red-400">{errors.targetIntake.message}</p>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-1">
                            English Proficiency Test *
                          </label>
                          <select
                            {...register('englishProficiency', { required: 'Please select English proficiency status' })}
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          >
                            <option value="">Select English test status</option>
                            {englishTests.map((test) => (
                              <option key={test} value={test}>{test}</option>
                            ))}
                          </select>
                          {errors.englishProficiency && (
                            <p className="mt-1 text-sm text-red-400">{errors.englishProficiency.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div className="border-t border-gray-600 pt-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Additional Information
                        </label>
                        <textarea
                          {...register('message')}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          placeholder="Tell us about your goals, preferences, or any specific requirements..."
                        />
                      </div>
                    </div>

                    {/* Referral Section */}
                    <div className="border-t border-gray-600 pt-6">
                      <h4 className="text-sm font-medium text-gray-300 mb-4 flex items-center">
                        <Star className="h-4 w-4 text-yellow-400 mr-2" />
                        Referral Information (Optional)
                      </h4>
                      
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Referral ID
                          </label>
                          <input
                            {...register('referralId')}
                            type="text"
                            className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                            placeholder="Enter referral ID"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Referral Name
                            </label>
                            <input
                              {...register('referralName')}
                              type="text"
                              className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                              placeholder="Who referred you?"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Referral Phone
                            </label>
                            <input
                              {...register('referralPhone')}
                              type="tel"
                              className="w-full px-3 py-2 border border-gray-600 bg-gray-800 text-white rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                              placeholder="Referrer's phone"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-gradient-to-r from-cyan-900/20 to-purple-900/20 border border-cyan-700/30 rounded-lg">
                        <p className="text-xs text-cyan-300 flex items-start">
                          <Star className="h-3 w-3 text-yellow-400 mr-1 mt-0.5 flex-shrink-0" />
                          Get special benefits and priority processing with valid referrals! Existing students and partners can refer you for exclusive study abroad packages.
                        </p>
                      </div>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 text-white py-3 px-4 rounded-lg hover:from-cyan-700 hover:to-purple-700 transition-all font-medium text-lg"
                    >
                      Submit Study Abroad Application
                    </motion.button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
    </UniversalFormWrapper>
  );
};

export default AbroadConsultancyForm;
